import type { CitationCheckDroppedEntry } from '@seen/ai-agents'
import type { JsonLike } from '@seen/core/json'

/* One dropped proposal as a tool result reports it back to the model: its own
 * words for where it wrote, and why the server's citation check dropped it. The
 * position is left out — it indexes the array the model sent, which is the
 * browser's business, not the model's. */
export interface CitationCheckDroppedReport {
  target: string
  reason: string
}

/* The drops the server wrote onto a refined tool input, read structurally:
 * the field is server-written, and anything that is not a well-formed entry
 * is not one the server wrote. */
export function citationCheckDroppedOf(
  input: JsonLike | undefined
): readonly CitationCheckDroppedEntry[] {
  if (typeof input !== 'object' || input === null) {
    return []
  }
  const { citationCheckDropped } = input as { citationCheckDropped?: unknown }
  if (!Array.isArray(citationCheckDropped)) {
    return []
  }
  return citationCheckDropped.filter(
    (entry): entry is CitationCheckDroppedEntry =>
      typeof entry === 'object' &&
      entry !== null &&
      Number.isInteger(entry.index) &&
      entry.index >= 0 &&
      typeof entry.target === 'string' &&
      typeof entry.reason === 'string'
  )
}

/* The position in the MODEL's array of each operation that survived, in
 * order: `survivingPositions(4, [{ index: 1 }])` is `[0, 2, 3]`.
 *
 * The editor's streaming paint staged ops off the model's raw deltas under
 * `${toolCallId}-${position}` before the server's refined input existed. The
 * refined input has the dropped ops removed, so a survivor staged under its
 * index in THAT array would land on the id a different op was painted under —
 * and a last-write-wins add would put its content over the other's. Staging it
 * under its original position instead is what makes the completing call
 * overwrite exactly what the stream painted for the same op. */
export function survivingPositions(
  survivorCount: number,
  dropped: readonly CitationCheckDroppedEntry[]
): number[] {
  const droppedPositions = new Set(dropped.map((entry) => entry.index))
  const positions: number[] = []
  for (let position = 0; positions.length < survivorCount; position += 1) {
    if (!droppedPositions.has(position)) {
      positions.push(position)
    }
  }
  return positions
}

/* What a tool result carries about the drops: nothing at all when there were
 * none, so a result is unchanged from before this existed. */
export function citationCheckDroppedReport(
  dropped: readonly CitationCheckDroppedEntry[]
): { citationCheckDropped: CitationCheckDroppedReport[] } | undefined {
  if (dropped.length === 0) {
    return undefined
  }
  return {
    citationCheckDropped: dropped.map(({ target, reason }) => ({
      target,
      reason,
    })),
  }
}

/* The sentence a result's summary gains when something was dropped. Said in
 * the result rather than in the tool's description, so a call that dropped
 * nothing — every call, with the check off — reads exactly as it always has. */
export function citationCheckDroppedSummary(
  dropped: readonly CitationCheckDroppedEntry[]
): string | undefined {
  if (dropped.length === 0) {
    return undefined
  }
  const count = dropped.length
  return `${count} proposal${count === 1 ? ' was' : 's were'} NOT staged: the server dropped ${count === 1 ? 'it' : 'them'} because ${count === 1 ? 'its' : 'their'} citation failed a check, so the clinician never sees ${count === 1 ? 'it' : 'them'}. \`citationCheckDropped\` names each one and why, the plain cause first, and that is the only reason ${count === 1 ? 'it was' : 'they were'} left out. Send one again only where its reason says to, on its own with the citation fixed; send no other again. Tell the clinician which lines you leave out and why, in the plain words before each check detail; never repeat an id or how the check works.`
}

/* The fields a propose tool's `staged` result gains when the server dropped
 * some of its answers. Both are absent otherwise. */
export interface CitationCheckDroppedResult {
  citationCheckDropped?: CitationCheckDroppedReport[]
  summary?: string
}

export function citationCheckDroppedResult(
  input: JsonLike | undefined
): CitationCheckDroppedResult {
  const dropped = citationCheckDroppedOf(input)
  const summary = citationCheckDroppedSummary(dropped)
  return summary === undefined
    ? {}
    : { ...citationCheckDroppedReport(dropped), summary }
}
